/**
 * SEO — Platform overview
 * TITLE: Platform | CSW Study Stream
 * H1: The CSW behavioral intelligence platform
 */
import { Activity, AlertTriangle, Workflow, Plug } from "lucide-react";
import { SEO } from "@/components/SEO";
import { InstLayout, Container, Eyebrow, SectionHeading, SignalCard, TrustBar } from "@/components/institutional/InstLayout";
import { Button } from "@/components/ui/button";

const Platform = () => (
  <InstLayout>
    <SEO
      title="Platform — Behavioral Intelligence for Student Success"
      description="The CSW platform surfaces engagement signals, risk indicators, and advisor workflows on top of your existing LMS and SIS — explainable, attributable, and designed for human oversight."
      canonicalUrl="/platform/"
    />
    <Container>
      <header className="max-w-3xl mb-16">
        <Eyebrow>Platform</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
          The CSW behavioral intelligence platform.
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
          A behavioral intelligence layer that sits alongside your LMS and SIS. CSW analyzes
          engagement signals within the platform and surfaces explainable indicators that help
          advisors decide where to reach out next.
        </p>
      </header>

      <section aria-labelledby="pillars" className="mb-20">
        <SectionHeading
          eyebrow="Four pillars"
          title={<span id="pillars">What the platform provides.</span>}
        />
        <div className="grid md:grid-cols-2 gap-6">
          <SignalCard
            icon={Activity}
            title="Engagement signals"
            description="Behavioral patterns within CSW — session cadence, focus consistency, participation rhythms, study habits across time."
          />
          <SignalCard
            icon={AlertTriangle}
            title="Risk indicators"
            description="Trend-based early-warning indicators that flag behavioral shifts commonly associated with disengagement. Every indicator is attributable to specific evidence."
          />
          <SignalCard
            icon={Workflow}
            title="Advisor workflows"
            description="Caseload triage views, explainable signal cards, contextual notes, and outreach support so advisors spend time on the right students."
          />
          <SignalCard
            icon={Plug}
            title="Integrations"
            description="Works with Canvas, Blackboard, Brightspace, Moodle, Google Classroom, and major SIS providers. CSW complements — it does not replace — your existing systems."
          />
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild variant="outline"><a href="/platform/engagement-signals/">Engagement signals →</a></Button>
          <Button asChild variant="outline"><a href="/platform/risk-indicators/">Risk indicators →</a></Button>
          <Button asChild variant="outline"><a href="/platform/advisor-workflows/">Advisor workflows →</a></Button>
          <Button asChild variant="outline"><a href="/platform/integrations/">Integrations →</a></Button>
        </div>
      </section>

      <TrustBar note="Every CSW signal is explainable and tied to specific behavioral evidence. CSW is decision-support — not a clinical, medical, or psychological evaluation system." />

      <section aria-labelledby="how" className="mb-20">
        <SectionHeading
          eyebrow="How it works"
          title={<span id="how">From raw behavioral data to advisor-ready context.</span>}
        />
        <ol className="space-y-6">
          {[
            { n: "01", t: "Engagement is captured in context", d: "CSW analyzes study activity within the platform alongside the academic context you choose to share from your LMS or SIS." },
            { n: "02", t: "Signals are derived, not assumed", d: "Behavioral patterns are aggregated into engagement signals over rolling windows. We never claim causation; we surface association." },
            { n: "03", t: "Indicators are reviewed by humans", d: "Risk indicators reach your advisors with explainable evidence cards so they can decide whether — and how — to reach out." },
            { n: "04", t: "Outcomes inform the loop", d: "Outreach decisions and outcomes can be logged back so your team can learn what supports actually help, with full audit trail." },
          ].map((step) => (
            <li key={step.n} className="rounded-2xl border border-border bg-card p-6 md:p-8 flex gap-6">
              <span className="font-display text-3xl font-bold text-primary/70 shrink-0">{step.n}</span>
              <div>
                <h3 className="font-display text-xl font-semibold mb-2">{step.t}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="rounded-3xl border border-border bg-muted/30 p-10 md:p-14 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">See the platform on your institution's data.</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">A guided walkthrough with a member of our team — typically 30 minutes.</p>
        <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-base"><a href="/demo/">Request a demo</a></Button>
      </section>
    </Container>
  </InstLayout>
);

export default Platform;
